import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { RootState, useAppSelector, useAppDispatch, loadChange, createComment, updateComment, deleteComment, toggleCommentReaction, loadAttachments } from '../../redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Timeline } from 'flowbite-react';
import { Log, Comment, User, Change, Attachment } from '../../models';
import { WysiwygField, CollapsibleQuotedHtml, OriginalEmailFrame, EmailHeadersDetails } from '../../components';
import { UserAvatar } from '../';
import { Icon } from '../Icon';
import MessageJumpRail, { MessageJumpItem } from './MessageJumpRail';
import { usePinnedRail } from './usePinnedRail';
import {
    activityPreviewLimit,
    commentsForJumpRail,
    isMessageInPreview,
    shouldShowMessageJumpRail,
} from '../../utils/messageJumpRail';
import {
    detectMessageLanguage,
    isForeignLanguage,
    normalizeUiLanguage,
} from '../../utils/detectMessageLanguage';
import { Changes } from '../../services';
import { formatLocalizedDate, formatMomentDate } from '../../locales/i18n';
import moment from 'moment';
import { Button } from '../Button';
import ReactTimeAgo from 'react-time-ago';
import {
    buildChangeActivityTimeline,
    groupActivityBurstsInItems,
    groupConsecutiveActivityItems,
    isActivityBurst,
    isGroupedActivityLog,
    GroupedActivityLog,
} from '../../utils/groupActivityLogs';
import { visibleMessageHtmlForQuote } from '../../utils/replyQuote';
import {
    attachmentsForForwardedComment,
    buildForwardAttachmentsHtml,
} from '../../utils/forwardAttachments';
import { isClientInboundMessage } from '../../utils/createLinkedChange';
import { ActivityBurstRow } from '../ActivityBurstRow';
import CommentReactions from './CommentReactions';

export type ReplyFromTimelinePayload = {
    messageHtml: string;
    /** Full name for citation line (never "Me" / localized self label). */
    authorLabel: string;
    /** First name when available — used in recipient warning copy. */
    authorFirstName?: string;
    authorEmail?: string | null;
    authorId?: string | null;
    /** Timeline comment id (required for forward attachment linkage). */
    commentId?: string;
    /** Prebuilt HTML listing source-message attachments (forward only). */
    forwardAttachmentsHtml?: string;
    /**
     * Where to open the parent composer when quoting this message.
     * Internal comments → comment tab; client-facing messages → reply tab.
     */
    target?: 'comment' | 'reply';
};

interface ChangeActivityProps {
    change: Change;
    members: User[];
    showComposer?: boolean;
    hideActivityToggle?: boolean;
    alwaysShowActivity?: boolean;
    /** Hide the fixed message-jump rail (e.g. while an external composer is open above). */
    hideJumpRail?: boolean;
    composerTitle?: string;
    /**
     * When set, timeline comments/replies get a control that lets the user quote the message
     * into an external composer owned by the parent component.
     */
    onReplyFromTimeline?: (payload: ReplyFromTimelinePayload) => void;
    /**
     * When set, timeline items get a Forward control that quotes the chosen message
     * into an external forward composer (empty recipients).
     */
    onForwardFromTimeline?: (payload: ReplyFromTimelinePayload) => void;
    /** Called after a new comment is successfully created (not on edit). */
    onCommentCreated?: () => void;
    /** Discard the comment draft and close an externally owned composer. */
    onComposerCancel?: () => void;
    /**
     * Unsaved new-comment HTML owned by the parent so Formik reinitialize
     * (Mercure / loadChange) does not wipe the draft mid-typing.
     */
    commentDraft?: string;
    onCommentDraftChange?: (html: string) => void;
    /**
     * When set, client/intake messages get a control to spawn a linked follow-up
     * card seeded with that message.
     */
    onCreateLinkedChange?: (comment: Comment) => void;
    /** Disable the linked-card control while creation is in progress. */
    creatingLinkedChange?: boolean;
}

function ChangeActivity(props: ChangeActivityProps) {
    const {
        change, members, showComposer = true, hideActivityToggle = false, alwaysShowActivity = false,
        hideJumpRail = false, composerTitle, onReplyFromTimeline, onForwardFromTimeline,
        onCommentCreated, onComposerCancel,
        commentDraft, onCommentDraftChange,
        onCreateLinkedChange, creatingLinkedChange = false,
    } = props;

    const { user }                = useAppSelector((state: RootState) => state.auth);
    const { updating }            = useAppSelector((state: RootState) => state.changes);
    const { groups }              = useAppSelector((state: RootState) => state.groups);
    const { attachments }         = useAppSelector((state: RootState) => state.attachments);
    const [ visible, setVisible ] = useState(alwaysShowActivity);
    const [ showAll, setShowAll ] = useState(false);
    const [ replyTo, setReplyTo ] = useState<Comment | null>(null);
    const [ originalViewIds, setOriginalViewIds ] = useState<Record<string, boolean>>({});
    const [ translatedViewIds, setTranslatedViewIds ] = useState<Record<string, boolean>>({});
    const [ translationCache, setTranslationCache ] = useState<Record<string, string>>({});
    const [ translatingIds, setTranslatingIds ] = useState<Record<string, boolean>>({});
    const [ activeJumpId, setActiveJumpId ] = useState<string | null>(null);
    const [ jumpNonce, setJumpNonce ] = useState(0);
    const pendingJumpIdRef = React.useRef<string | null>(null);
    const today                   = moment();

    const { hash }    = useLocation();
    const scrolledRef = React.useRef(false);
    const hashRef     = React.useRef(hash);

    const navigate    = useNavigate();
    const { t, i18n } = useTranslation();

    const items = React.useMemo(() => {
        if (!change)
            return [];

        const indexed = (change.comments || []).map((item: AnyObj, i: number) => (new Comment({
            ...item,
            commentIndex: i
        })));

        // Chronological merge: nest activity logs under the nearest preceding
        // root message so logs sit with messages instead of as a flat parallel list.
        return buildChangeActivityTimeline({
            comments: indexed as Comment[],
            logs: change.logs || [],
            includeLogs: visible,
            hideInternalComments: user?.role === User.ROLE_CLIENT,
        });
    }, [change, visible, user?.role]);

    const jumpToMessage = (id: string) => {
        pendingJumpIdRef.current = id;
        setActiveJumpId(id);
        scrolledRef.current = false;
        if (!showAll)
            setShowAll(true);
        setJumpNonce((nonce) => nonce + 1);
        navigate(`#${id}`);
    };

    const attachmentsByComment = useMemo(() => {
        const map = new Map<string, Attachment[]>();
        (attachments || []).forEach((raw: AnyObj) => {
            const attachment = raw instanceof Attachment ? raw : new Attachment(raw);
            const commentId = attachment.comment;
            if (!commentId || String(attachment.change?._id || attachment.change || '') !== String(change?._id))
                return;
            const list = map.get(commentId) || [];
            list.push(attachment);
            map.set(commentId, list);
        });
        return map;
    }, [attachments, change?._id]);

    useEffect(() => {
        const id = pendingJumpIdRef.current || (hash ? hash.replace('#', '') : '');
        if (!id)
            return;
        if (!items.some((item: AnyObj) => item?._id === id))
            return;
        if (!isMessageInPreview(items, id, showAll)) {
            setShowAll(true);
            scrolledRef.current = false;
            return;
        }
        if (hash && hashRef.current !== hash) {
            hashRef.current = hash;
            scrolledRef.current = false;
        }
        if (!scrolledRef.current || pendingJumpIdRef.current === id) {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                scrolledRef.current = true;
                pendingJumpIdRef.current = null;
            }
        }
    }, [items, showAll, hash, jumpNonce]);

    const latestEditableCommentId = React.useMemo(() => {
        const list = (change?.comments || [])
            .map((item: AnyObj) => new Comment(item))
            .filter((comment: Comment) => !comment.isOutbound() && !comment.isRtImport() && !comment.isEmailIntake())
            .sort((a: Comment, b: Comment) => a.createdAt.isBefore(b.createdAt) ? 1 : -1);
        return list[0]?._id;
    }, [change?.comments?.length, change?.updatedAt]);

    const dispatch    = useAppDispatch();

    useEffect(() => {
        if (!change?._id)
            return;
        dispatch(loadAttachments({ change: change._id }));
    }, [change?._id, change?.nbAttachments, dispatch]);

    const onSaveComment = async (value: string, _id?: string) => {
        if (_id)
            return await dispatch(updateComment({ _id, message: value }, /*patch*/true));

        await dispatch(createComment(
            {
                change,
                project: change.project,
                message: value,
                threadId: replyTo?.threadId
            },
            undefined,
            (err) => {
                if (err)
                    return;
                setReplyTo(null);
                onCommentDraftChange?.('');
                onCommentCreated?.();
            }
        ));
    }

    const newCommentDraftProps = onCommentDraftChange
        ? { draftValue: commentDraft ?? '', onDraftChange: onCommentDraftChange }
        : {};
    const onDeleteComment = async (_id: string) => {
        await dispatch(deleteComment(_id, () => {
            dispatch(loadChange(change._id));
        }));
    }

    const handleActivity = () => {
        setVisible(!visible);
        setShowAll(false);
    };

    const mentions = members.map((user: User) => ({
        text: user.fullname(),
        value: user.username(),
        url: '#'
    }));

    /**
     * Returns the author display label for UI activity strings.
     * Uses the localized self label (e.g. "Me") when applicable.
     */
    const displayAuthorName = (author?: User) => {
        if (!author?._id)
            return '';
        return author._id === user?._id ? (t('common.me') || '') : [author.firstname, author.lastname].filter(s => !!s).join(' ');
    };

    /**
     * Returns the author full name for quoting/citation purposes.
     * Important: this must never use localized self labels ("Me"/"Moi"), so the quote stays stable.
     */
    const authorFullNameForQuote = (author?: User) => {
        if (!author?._id)
            return '';
        return [author.firstname, author.lastname].filter(s => !!s).join(' ');
    };

    /**
     * Resolves raw recipient tokens into display labels.
     *
     * Supported formats:
     * - "@<userId>" → "@Full Name" (when member exists)
     * - "#<groupId>" → "#Group Name" (when group exists)
     * - other strings are returned as-is
     */
    const resolveRecipientLabel = (raw: string) => {
        const value = String(raw || '').trim();
        if (!value)
            return '';
        if (value.startsWith('@')) {
            const id = value.slice(1);
            const member = members.find((m: User) => m?._id === id);
            if (member)
                return `@${member.fullname()}`;
            return value;
        }
        if (value.startsWith('#')) {
            const id = value.slice(1);
            const group = (groups || []).find((g: AnyObj) => g?._id === id);
            if (group?.name)
                return `#${group.name}`;
            return value;
        }
        return value;
    };

    /**
     * Formats the `replyRecipients` structure into human-friendly "To" and "Cc" strings,
     * while also returning the underlying label arrays for conditional rendering.
     */
    const formatToCc = (value?: AnyObj) => {
        const flatten = (section: AnyObj) => {
            if (Array.isArray(section))
                return section;
            return [
                ...(Array.isArray(section?.emails) ? section.emails : []),
                ...(Array.isArray(section?.signalUsers) ? section.signalUsers.map((item: string) => `@${item}`) : []),
                ...(Array.isArray(section?.signalGroups) ? section.signalGroups.map((item: string) => `#${item}`) : []),
            ];
        };
        const toList = flatten(value?.to)
            .map(resolveRecipientLabel)
            .filter((item: string) => !!item);
        const ccList = flatten(value?.cc)
            .map(resolveRecipientLabel)
            .filter((item: string) => !!item);
        const to = toList.join(', ');
        const cc = ccList.join(', ');
        return { to, cc, toList, ccList };
    };

    /**
     * Renders a compact time label for activity items.
     * Uses relative time for recent events, and a localized date beyond a threshold.
     */
    const renderActivityTime = (createdAt: AnyObj) => (
        <div className="text-xs italic">
            {(moment.duration(today.diff(createdAt)).asHours() < 48) ?
                <ReactTimeAgo locale={i18n.language} date={createdAt.toDate()} /> :
                `${t('logs.on')} ${formatMomentDate(createdAt, 'll')}`
            }
        </div>
    );

    /**
     * Renders the header line for replies/forwards, including recipients (To/Cc) when available.
     * This is shown instead of the generic "comment added" label for outbound items.
     */
    const renderReplyHeader = (item: Comment) => {
        const { to, cc, toList, ccList } = formatToCc(item.replyRecipients);
        const isSelf = item.author?._id && item.author._id === user?._id;
        let main = '';
        if (item.isForward()) {
            if (isSelf)
                main = t('changes.activity_forward_sent_self') || '';
            else
                main = t('changes.activity_forward_sent', { author: displayAuthorName(item.author) }) || '';
        } else if (item.isRtImport())
            main = t('changes.activity_reply_sent_rt', { author: item.externalAuthorLabel() || displayAuthorName(item.author) }) || '';
        else if (isSelf)
            main = user?.role === User.ROLE_CLIENT ? (t('changes.activity_message_sent_self') || '') : (t('changes.activity_reply_sent_self') || '');
        else
            main = t('changes.activity_reply_sent', { author: displayAuthorName(item.author) }) || '';
        return (
            <div className="w-full">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-bold text-left">{main}</div>
                    {renderActivityTime(item.createdAt)}
                </div>
                {(toList.length > 0 || ccList.length > 0) && (
                    <div className="text-xs italic text-left text-gray-600 dark:text-gray-300">
                        {toList.length > 0 && (
                            <>
                                <span className="mr-1">{t('changes.reply_recipients_to')}:</span>
                                {to}
                            </>
                        )}
                        {ccList.length > 0 && (
                            <>
                                {toList.length > 0 && <span className="mx-1" />}
                                <span className="mr-1">{t('changes.reply_recipients_cc')}:</span>
                                {cc}
                            </>
                        )}
                        .
                    </div>
                )}
            </div>
        );
    };

    /**
     * Builds the HTML activity label for comments and replies.
     * For replies, includes recipients when possible.
     */
    const commentEventLabel = (item: Comment) => {
        const author = displayAuthorName(item.author);
        const isSelf = item.author?._id && item.author._id === user?._id;
        if (item.isRtImport()) {
            const rtAuthor = item.externalAuthorLabel() || author;
            if (item.isReply())
                return t('changes.activity_reply_sent_rt', { author: rtAuthor }) || '';
            return t('changes.activity_comment_added_rt', { author: rtAuthor }) || '';
        }
        if (item.isEmailIntake()) {
            const intakeAuthor = item.externalAuthorLabel() || author;
            if (item.isReply())
                return t('changes.activity_reply_sent_email', { author: intakeAuthor }) || '';
            return t('changes.activity_comment_added_email', { author: intakeAuthor }) || '';
        }
        if (item.isForward()) {
            const { to, cc } = formatToCc(item.replyRecipients);
            if (isSelf)
                return t('changes.activity_forward_sent_self') || '';
            return t('changes.activity_forward_sent_with_recipients', { author, to, cc }) || '';
        }
        if (item.isReply()) {
            const { to, cc } = formatToCc(item.replyRecipients);
            if (isSelf)
                return user?.role === User.ROLE_CLIENT ? (t('changes.activity_message_sent_self') || '') : (t('changes.activity_reply_sent_self') || '');
            return t('changes.activity_reply_sent_with_recipients', { author, to, cc }) || '';
        }
        if (isSelf)
            return t('changes.activity_comment_added_self') || '';
        return t('changes.activity_comment_added', { author }) || '';
    };

    /**
     * Determines whether a comment message is non-empty once HTML is stripped.
     * Used to decide if a "Reply/Quote" control should be displayed.
     */
    const hasQuotableMessage = (item: Comment) => {
        const raw = (item.message || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        return raw.length > 0;
    };

    /**
     * HTML bodies that existed on the card before this comment (description locales
     * + earlier comments). Used to detect quoted history via real overlap.
     */
    const previousHtmlsForComment = (item: Comment): string[] => {
        const previous: string[] = [];
        const description = change?.description;
        if (description && typeof description === 'object') {
            Object.values(description).forEach((value) => {
                if (typeof value === 'string' && value.trim())
                    previous.push(value);
            });
        }
        (change?.comments || []).forEach((raw: AnyObj) => {
            const comment = raw instanceof Comment ? raw : new Comment(raw);
            if (!comment?._id || comment._id === item._id)
                return;
            if (!comment.createdAt || !item.createdAt)
                return;
            if (!comment.createdAt.isBefore(item.createdAt))
                return;
            if (comment.message)
                previous.push(comment.message);
        });
        return previous;
    };

    const onTranslateComment = async (item: Comment) => {
        const commentId = item._id;
        const showingTranslation = !!translatedViewIds[commentId];

        // Toggle back to source when already showing a translation.
        if (showingTranslation) {
            setTranslatedViewIds((prev) => ({ ...prev, [commentId]: false }));
            return;
        }

        // Translation always uses the simplified message view.
        setOriginalViewIds((prev) => ({ ...prev, [commentId]: false }));

        if (translationCache[commentId]) {
            setTranslatedViewIds((prev) => ({ ...prev, [commentId]: true }));
            return;
        }

        const to = normalizeUiLanguage(i18n.language);
        if (!to)
            return;

        setTranslatingIds((prev) => ({ ...prev, [commentId]: true }));
        try {
            const translated = await Changes.translate({
                text: item.message,
                to,
                context: 'Translate this customer support email body. Keep HTML tags.',
            });
            setTranslationCache((prev) => ({ ...prev, [commentId]: translated }));
            setTranslatedViewIds((prev) => ({ ...prev, [commentId]: true }));
        } catch (err) {
            console.error('Failed to translate comment message', err);
        } finally {
            setTranslatingIds((prev) => ({ ...prev, [commentId]: false }));
        }
    };

    const renderCommentAttachments = (item: Comment) => {
        const list = attachmentsByComment.get(item._id) || [];
        if (!list.length)
            return null;

        return (
            <ul className="mt-2 space-y-1 border-t border-gray-200 dark:border-gray-700 pt-2">
                {list.map((attachment) => (
                    <li key={attachment._id}>
                        <a
                            href={attachment.downloadUrl()}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300 hover:underline"
                        >
                            <Icon type="file" size={3} className="inline shrink-0" />
                            <span className="font-medium">{attachment.name}</span>
                            {attachment.isPicture() ? null : (
                                <span className="uppercase text-gray-500 dark:text-gray-400">
                                    {attachment.getExtension()}
                                </span>
                            )}
                        </a>
                    </li>
                ))}
            </ul>
        );
    };

    const renderCommentMessage = (item: Comment) => {
        const hasOriginal = !!(item.originalMessage && String(item.originalMessage).trim());
        const hasHeaders = !!(item.headers && (
            item.headers.raw ||
            item.headers.from ||
            item.headers.auth ||
            item.headers.replyTo ||
            item.headers.returnPath ||
            item.headers.xSpamStatus ||
            item.headers.xSpamScore
        ));
        const showOriginal = hasOriginal && !!originalViewIds[item._id];
        const showTranslate = isForeignLanguage(
            detectMessageLanguage(item.message),
            i18n.language
        );
        const showTranslation = !showOriginal && !!translatedViewIds[item._id] && !!translationCache[item._id];
        const isTranslating = !!translatingIds[item._id];
        const displayHtml = showTranslation ? translationCache[item._id] : item.message;

        return (
            <div
                className="content text-sm text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded p-2 min-w-0 overflow-x-auto bg-gray-50 dark:bg-gray-800"
            >
                {(hasOriginal || showTranslate || hasHeaders) && (
                    <div className="mb-2 space-y-2">
                        {(hasOriginal || showTranslate) && (
                            <div className="flex flex-wrap gap-2">
                                {hasOriginal && (
                                    <button
                                        type="button"
                                        className="inline-flex items-center text-xs font-normal text-gray-600 hover:text-gray-900 dark:text-gray-100 hover:underline bg-transparent border border-gray-200 dark:border-gray-700 rounded px-2 py-1"
                                        onClick={() => {
                                            setOriginalViewIds((prev) => ({
                                                ...prev,
                                                [item._id]: !prev[item._id],
                                            }));
                                            // Leaving original view does not force translation off.
                                            if (!originalViewIds[item._id]) {
                                                setTranslatedViewIds((prev) => ({
                                                    ...prev,
                                                    [item._id]: false,
                                                }));
                                            }
                                        }}
                                    >
                                        <Icon type="eye" className="inline mr-1" size={3} />
                                        {showOriginal
                                            ? (t('changes.show_simplified_message') || 'Show simplified version')
                                            : (t('changes.show_original_message') || 'Show original')}
                                    </button>
                                )}
                                {showTranslate && (
                                    <button
                                        type="button"
                                        className="inline-flex items-center text-xs font-normal text-gray-600 hover:text-gray-900 dark:text-gray-100 hover:underline bg-transparent border border-gray-200 dark:border-gray-700 rounded px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={isTranslating}
                                        onClick={() => onTranslateComment(item)}
                                    >
                                        <Icon type="globe" className="inline mr-1" size={3} />
                                        {isTranslating
                                            ? (t('changes.translating_message') || 'Translating…')
                                            : (showTranslation || translatedViewIds[item._id]
                                                ? (t('changes.show_source_message') || 'Show source')
                                                : (t('changes.translate_message') || 'Translate'))}
                                    </button>
                                )}
                            </div>
                        )}
                        {hasHeaders && (
                            <EmailHeadersDetails headers={item.headers} showFailBadges />
                        )}
                    </div>
                )}
                {showOriginal ? (
                    <OriginalEmailFrame html={item.originalMessage || ''} />
                ) : (
                    <CollapsibleQuotedHtml
                        html={displayHtml}
                        previousHtmls={previousHtmlsForComment(item)}
                        projectId={change?.project?._id}
                    />
                )}
                {renderCommentAttachments(item)}
            </div>
        );
    };

    const renderCommentReactions = (item: Comment) => {
        // Heart only on internal comments — not outbound, email intake, or RT imports.
        if (
            item.isOutbound() ||
            item.isEmailIntake() ||
            item.isRtImport() ||
            user?.role === User.ROLE_CLIENT
        )
            return null;

        const canReact = item.author?._id !== user?._id;

        return (
            <CommentReactions
                comment={item}
                user={user}
                canReact={canReact}
                onToggle={(emoji) => {
                    void dispatch(toggleCommentReaction(item._id, emoji));
                }}
            />
        );
    };

    /**
     * Returns the author label to use in the quote/citation line.
     * Prefers a stable full name; falls back to `authorName()` when needed.
     */
    const authorLabelForQuote = (item: Comment) => {
        const full = authorFullNameForQuote(item.author);
        if (full)
            return full;
        return item.authorName() || '';
    };

    /**
     * Client-facing timeline messages (email replies, intake, RT) quote into the
     * reply composer. Internal comments quote into the comment composer.
     */
    const timelineQuoteTarget = (item: Comment): 'comment' | 'reply' => {
        if (item.isReply() || item.isEmailIntake() || item.isRtImport())
            return 'reply';
        return 'comment';
    };

    const buildTimelineQuotePayload = (item: Comment): ReplyFromTimelinePayload => ({
        // Cite only fresh content — nested history would top-post as
        // [new reply][oldest][middle] when re-quoted.
        messageHtml: visibleMessageHtmlForQuote(
            item.message || '',
            previousHtmlsForComment(item),
        ),
        authorLabel: authorLabelForQuote(item),
        authorFirstName: item.author?.firstname || '',
        authorEmail: item.author?.email || null,
        authorId: item.author?._id || null,
        commentId: item._id,
        target: timelineQuoteTarget(item),
    });

    /**
     * Renders a small quote control next to a timeline item that triggers the
     * parent-owned composer via `onReplyFromTimeline`.
     * Internal comments use "Commenter"; client-facing messages use "Répondre".
     */
    const renderReplyFromTimelineControl = (item: Comment) => {
        if (!onReplyFromTimeline || !hasQuotableMessage(item))
            return null;
        const target = timelineQuoteTarget(item);
        const label = target === 'comment'
            ? (t('changes.communication_comment') || t('common.reply'))
            : t('common.reply');
        return (
            <button
                type="button"
                className="inline-flex items-center leading-none text-xs font-normal text-gray-500 dark:text-gray-400 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                onClick={() => onReplyFromTimeline(buildTimelineQuotePayload(item))}
            >
                <Icon type="reply" className="inline mr-0.5 text-gray-500 dark:text-gray-400" size={3} />
                {label}
            </button>
        );
    };

    /**
     * Renders a "Forward" control next to a timeline item for the parent forward composer.
     */
    const renderForwardFromTimelineControl = (item: Comment) => {
        if (!onForwardFromTimeline || !hasQuotableMessage(item))
            return null;
        return (
            <button
                type="button"
                className="inline-flex items-center leading-none text-xs font-normal text-gray-500 dark:text-gray-400 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                onClick={() => {
                    const payload = buildTimelineQuotePayload(item);
                    const changeId = String(change?._id || '');
                    const sourceAttachments = attachmentsForForwardedComment(
                        (attachments || []).map((raw: AnyObj) => (
                            raw instanceof Attachment ? raw : new Attachment(raw)
                        )),
                        changeId,
                        item,
                    );
                    onForwardFromTimeline({
                        ...payload,
                        forwardAttachmentsHtml: buildForwardAttachmentsHtml(
                            sourceAttachments,
                            t('changes.forward_attachments_label') || 'Pièces jointes :',
                        ),
                    });
                }}
            >
                <Icon type="email" className="inline mr-0.5 text-gray-500 dark:text-gray-400" size={3} />
                {t('common.forward')}
            </button>
        );
    };

    const renderComposerReplyControl = (item: Comment) => {
        if (!showComposer || item.isOutbound())
            return null;

        if (replyTo?._id === item._id) {
            return (
                <button
                    type="button"
                    className="inline-flex items-center leading-none text-xs font-normal text-gray-500 dark:text-gray-400 bg-transparent border-0 p-0 cursor-pointer"
                    onClick={() => setReplyTo(null)}
                >
                    <Icon type="close" className="inline mr-0.5 text-gray-500 dark:text-gray-400" size={3} />
                    <span className="hover:underline">{t('common.cancel')}</span>
                </button>
            );
        }

        return (
            <button
                type="button"
                className="inline-flex items-center leading-none text-xs font-normal text-gray-500 dark:text-gray-400 bg-transparent border-0 p-0 cursor-pointer"
                onClick={() => setReplyTo(item)}
            >
                <Icon type="reply" className="inline mr-0.5 text-gray-500 dark:text-gray-400" size={3} />
                <span className="hover:underline">{t('common.reply')}</span>
            </button>
        );
    };

    const renderCreateLinkedChangeControl = (item: Comment) => {
        if (!onCreateLinkedChange || !isClientInboundMessage(item))
            return null;
        return (
            <button
                type="button"
                disabled={creatingLinkedChange}
                className="inline-flex items-center leading-none text-xs font-normal text-gray-500 dark:text-gray-400 hover:underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                onClick={() => onCreateLinkedChange(item)}
            >
                <Icon type="branch" className="inline mr-0.5 text-gray-500 dark:text-gray-400" size={3} />
                <span>{t('changes.create_linked_change')}</span>
            </button>
        );
    };

    /**
     * Action row above the message body so "Répondre" / "Créer une carte liée"
     * stay reachable without scrolling. Hearts stay on the bottom action row only.
     */
    const renderTopReplyControls = (item: Comment, options: { includeComposerReply?: boolean } = {}) => {
        const { includeComposerReply = false } = options;
        const timelineReply = renderReplyFromTimelineControl(item);
        const timelineForward = renderForwardFromTimelineControl(item);
        const composerReply = includeComposerReply ? renderComposerReplyControl(item) : null;
        const createLinked = renderCreateLinkedChangeControl(item);
        if (!timelineReply && !timelineForward && !composerReply && !createLinked)
            return null;

        return (
            <div className="mb-1 flex flex-wrap items-center gap-2 leading-none">
                {timelineReply}
                {timelineForward}
                {composerReply}
                {createLinked}
            </div>
        );
    };

    /** Heart reactions + reply controls on one action row. Never show heart without Reply. */
    const renderCommentActions = (item: Comment, options: { includeComposerReply?: boolean } = {}) => {
        const { includeComposerReply = false } = options;
        const timelineReply = renderReplyFromTimelineControl(item);
        const timelineForward = renderForwardFromTimelineControl(item);
        const composerReply = includeComposerReply ? renderComposerReplyControl(item) : null;
        const createLinked = renderCreateLinkedChangeControl(item);
        const hasReplyControl = !!(timelineReply || composerReply);
        // Product rule: never show the reaction heart without a Reply control next to it
        // (e.g. pending triage disables onReplyFromTimeline while showComposer is false).
        const reactions = hasReplyControl ? renderCommentReactions(item) : null;

        if (!reactions && !timelineReply && !timelineForward && !composerReply && !createLinked)
            return null;

        return (
            <div className="mt-1 flex flex-wrap items-center gap-2 leading-none">
                {reactions}
                {timelineReply}
                {timelineForward}
                {composerReply}
                {createLinked}
            </div>
        );
    };

    const describeLogHtml = (item: Log) => {
        const isReplyRecipientsPatch = (
            item.type === Log.TYPE_CHANGE_PATCH &&
            item.context?.field === 'replyRecipients'
        );
        if (!isReplyRecipientsPatch)
            return item.describe();

        const isSelfAuthor = !!(item.author?._id && item.author._id === user?._id);
        return t(
            isSelfAuthor
                ? 'changes.activity_recipients_changed_with_lists_self'
                : 'changes.activity_recipients_changed_with_lists',
            {
                author: displayAuthorName(item.author),
                ...formatToCc(item.context?.value)
            }
        ) || '';
    };

    const renderActivityTimestamp = (createdAt: moment.Moment) => (
        (moment.duration(today.diff(createdAt)).asHours() < 48)
            ? <ReactTimeAgo locale={i18n.language} date={createdAt.toDate()}/>
            : `${t('logs.on')} ${formatMomentDate(createdAt, 'll')}`
    );

    const renderLogItem = (item: Log, count = 1, createdAt = item.createdAt) => {
        const isTrashLog = (
            item.type === Log.TYPE_CHANGE_DISCARDED ||
            item.type === Log.TYPE_CHANGE_RESTORED
        );
        // Keep log avatars on the same left gutter as root messages (no nest indent).
        return (
            <Timeline.Item
                key={item._id}
                className="mb-0"
                theme={{ "root": { "vertical": "mb-6 mt-6 ml-6" }}}
            >
                <Timeline.Point
                    icon={() => (
                        isTrashLog
                            ? <Icon type="delete" className="text-gray-500 dark:text-gray-400" size={4} />
                            : <UserAvatar user={item.author} size={7} hideInitials />
                    )}
                />
                <Timeline.Content>
                    <Timeline.Body>
                        <div className='text-sm'>
                            <span dangerouslySetInnerHTML={{ __html: describeLogHtml(item) }} />
                            {count > 1 && (
                                <span className="text-gray-500 dark:text-gray-400">
                                    {' '}
                                    {t('logs.repeat_count', { count })}
                                </span>
                            )}
                        </div>
                        <div className='text-xs italic'>
                            {renderActivityTimestamp(createdAt)}
                        </div>
                    </Timeline.Body>
                </Timeline.Content>
            </Timeline.Item>
        );
    };

    const renderBurstItem = (entry: GroupedActivityLog) => {
        const log = entry.log;
        const summaryHtml = t('logs.burst_summary', {
            firstname: log.author?.firstname || displayAuthorName(log.author),
        });

        return (
            <Timeline.Item
                key={`burst-${log._id}`}
                className="mb-0"
                theme={{ "root": { "vertical": "mb-6 mt-6 ml-6" }}}
                data-testid="activity-burst-item"
            >
                <Timeline.Point
                    icon={() => (
                        <UserAvatar user={log.author} size={7} hideInitials />
                    )}
                />
                <Timeline.Content>
                    <Timeline.Body>
                        <ActivityBurstRow
                            entry={entry}
                            summaryHtml={summaryHtml}
                            describeMember={(member) => describeLogHtml(member.log)}
                            renderTimestamp={renderActivityTimestamp}
                        />
                    </Timeline.Body>
                </Timeline.Content>
            </Timeline.Item>
        );
    };

    const renderItem = (item: AnyObj | GroupedActivityLog) => {
        if (isGroupedActivityLog(item)) {
            if (isActivityBurst(item))
                return renderBurstItem(item);
            return renderLogItem(item.log, item.count, item.createdAt);
        }

        if (item instanceof Log)
            return renderLogItem(item);

        if (item instanceof Comment)
            return (
                <Timeline.Item id={item._id} key={item._id} className={`scroll-mt-4 ${(item.level || 0) > 0 ? 'ml-[60px]' : ''}  mb-0 ${hash === ('#' + item._id) || activeJumpId === item._id ? 'bg-blue-100 dark:bg-blue-900/40 p-2' : ''}`} theme={{ "root": { "vertical": "mb-6 mt-6 ml-6" }}}>
                    <Timeline.Point className={`${(item.level || 0) > 0 ? 'relative -ml-6' : ''}`} icon={() => (
                        <UserAvatar user={item.author} size={7} hideInitials onClick={() => navigate(`#${item._id}`)}/>
                    )} />
                    <Timeline.Content>
                        <Timeline.Body>
                            {item.author?._id !== user?._id && (
                                <div className="Comment">
                                    {item.isOutbound() ? (
                                        renderReplyHeader(item)
                                    ) : (
                                        <div className="flex flex-row items-center justify-between gap-2">
                                            <div className='text-sm font-bold text-left' dangerouslySetInnerHTML={{ __html: commentEventLabel(item) }} />
                                            {renderActivityTime(item.createdAt)}
                                        </div>
                                    )}
                                    {renderTopReplyControls(item, { includeComposerReply: true })}
                                    {renderCommentMessage(item)}
                                    {renderCommentActions(item, { includeComposerReply: true })}
                                    {showComposer && !item.isOutbound() && replyTo?._id === item._id && (
                                        <div className="my-2 flex flex-row space-x-2 items-start">
                                            <div className="mt-1">
                                                <UserAvatar user={user} size={7} hideInitials />
                                            </div>
                                            <div className="flex-1">
                                                <WysiwygField
                                                    name={`comments[${change?.comments?.length}]`}
                                                    minimal
                                                    mentions={mentions}
                                                    placeholder={t('changes.enter_comment') || ''}
                                                    rows={8}
                                                    containerClassName="mb-2"
                                                    contentStyle={undefined}
                                                    className="text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6 Wysiwyg--comment"
                                                    onSave={ onSaveComment }
                                                    smallButtons={false}
                                                    hideCancelButton
                                                    uploadHideFiles
                                                    isLoading={ updating === 'pending' }
                                                    uploadProps={{
                                                        projectId: change?.project?._id,
                                                        changeId: change?._id
                                                    }}
                                                    {...newCommentDraftProps}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {item.author?._id === user?._id && !item.isOutbound() && !item.isRtImport() && !item.isEmailIntake() && item._id === latestEditableCommentId && (
                                <WysiwygField
                                    name={`comments[${item.commentIndex}].message`}
                                    placeholder={t('changes.enter_comment') || ''}
                                    rows={4}
                                    inline
                                    minimal
                                    mentions={mentions}
                                    label={(
                                        <div className="flex flex-row items-center justify-between gap-2">
                                            <div className='text-sm font-bold text-left' dangerouslySetInnerHTML={{ __html: commentEventLabel(item) }} />
                                            {renderActivityTime(item.createdAt)}
                                        </div>
                                    )}
                                    containerClassName="Comment -mt-1 mb-0"
                                    contentStyle={undefined}
                                    className="text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6 Wysiwyg--surface"
                                    onSave={ (value: string) => onSaveComment(value, item._id) }
                                    onDelete={ () => onDeleteComment (item._id)}
                                    smallButtons={false}
                                    hideCancelButton
                                    uploadHideFiles
                                    isLoading={ updating === 'pending' }
                                    uploadProps={{
                                        projectId: change?.project?._id,
                                        changeId: change?._id
                                    }}
                                />
                            )}
                            {item.author?._id === user?._id && !item.isOutbound() && item._id === latestEditableCommentId && renderCommentActions(item)}
                            {item.author?._id === user?._id && (item.isOutbound() || item._id !== latestEditableCommentId) && (
                                <div className="Comment">
                                    {item.isOutbound() ? (
                                        renderReplyHeader(item)
                                    ) : (
                                        <div className="flex flex-row items-center justify-between gap-2">
                                            <div className='text-sm font-bold text-left' dangerouslySetInnerHTML={{ __html: commentEventLabel(item) }} />
                                            {renderActivityTime(item.createdAt)}
                                        </div>
                                    )}
                                    {renderTopReplyControls(item)}
                                    {renderCommentMessage(item)}
                                    {renderCommentActions(item)}
                                </div>
                            )}
                        </Timeline.Body>
                    </Timeline.Content>
                </Timeline.Item>
            );

        return null;
    };

    const previewLimit = useMemo(() => activityPreviewLimit(items || []), [items]);

    const itemsToShow = useMemo(() => {
        const visibleItems = showAll ? items : items?.slice(0, previewLimit);
        return groupActivityBurstsInItems(
            groupConsecutiveActivityItems(visibleItems || []),
            undefined,
            { requireSameChange: false }
        );
    }, [items, showAll, previewLimit]);

    const jumpMessages: MessageJumpItem[] = useMemo(() => {
        return commentsForJumpRail(items).map((comment) => {
            const label = (comment.isRtImport() || comment.isEmailIntake())
                ? (comment.externalAuthorLabel() || displayAuthorName(comment.author))
                : displayAuthorName(comment.author) || comment.authorName();
            const dateLabel = comment.createdAt
                ? formatLocalizedDate(comment.createdAt.toDate(), i18n.language)
                : '';
            return {
                id: comment._id,
                author: comment.author,
                label,
                dateLabel,
                isEmail: comment.isOutbound() || comment.isEmailIntake() || comment.isRtImport(),
                isSelf: !!(comment.author?._id && comment.author._id === user?._id),
            };
        });
    }, [items, user?._id, i18n.language]);

    useEffect(() => {
        const comments = commentsForJumpRail(items);
        if (!shouldShowMessageJumpRail(comments.length))
            return undefined;

        const nodes = comments
            .map((comment) => document.getElementById(comment._id))
            .filter((node): node is HTMLElement => !!node);
        if (!nodes.length)
            return undefined;

        let root: HTMLElement | null = nodes[0].parentElement;
        while (root) {
            const overflowY = window.getComputedStyle(root).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll')
                break;
            root = root.parentElement;
        }

        const observer = new IntersectionObserver((entries) => {
            const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            const id = visible[0]?.target?.id;
            if (id)
                setActiveJumpId(id);
        }, {
            root: root || null,
            rootMargin: '-15% 0px -55% 0px',
            threshold: [0, 0.25, 0.5],
        });

        nodes.forEach((node) => observer.observe(node));
        return () => observer.disconnect();
    }, [items, showAll, itemsToShow]);

    const jumpRailEligible = shouldShowMessageJumpRail(jumpMessages.length);
    const showJumpRail = !hideJumpRail && jumpRailEligible;
    const { wrapperRef, pin } = usePinnedRail(showJumpRail, jumpMessages.length);

    return (
        <div className="">
            <div>
                {showComposer && !replyTo?._id && (
                    <div className="mt-0 mb-2">
                        <label className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 mb-2 block">
                            <Icon type="user" className="inline mr-1.5" />
                            {composerTitle || t('changes.add_comment')}
                        </label>
                        <div className="flex-1">
                            <WysiwygField
                                name={`comments[${change?.comments?.length}]`}
                                minimal
                                mentions={mentions}
                                placeholder={t('changes.enter_comment') || ''}
                                rows={8}
                                containerClassName="mb-2"
                                contentStyle={undefined}
                                className="text-gray-900 dark:text-gray-100 placeholder:text-gray-400 sm:text-sm sm:leading-6 Wysiwyg--comment"
                                onSave={ onSaveComment }
                                onClose={onComposerCancel}
                                smallButtons={false}
                                hideCancelButton={!onComposerCancel}
                                uploadHideFiles
                                isLoading={ updating === 'pending' }
                                uploadProps={{
                                    projectId: change?.project?._id,
                                    changeId: change?._id
                                }}
                                {...newCommentDraftProps}
                            />
                        </div>
                    </div>
                )}
                {!hideActivityToggle && (
                    <div className='text-left'>
                        <span
                            onClick={() => handleActivity()}
                            className="text-gray-600 dark:text-gray-300 text-sm font-bold cursor-pointer"
                        >
                            <Icon type={visible ? 'down' : 'right'} className="inline mr-1.5"/>
                            {`${ visible ? t('logs.hide_activity') : t('logs.show_activity') }`}
                        </span>
                    </div>
                )}
                {(visible || alwaysShowActivity) && (
                    <div ref={wrapperRef} className="relative">
                        <div className={jumpRailEligible ? 'md:pr-12' : undefined}>
                        <Timeline className="ml-4 my-0 mt-5 p-0" theme={{ root: { direction: { vertical: "relative border-l-0" }}}}>
                            { itemsToShow?.map((item) => renderItem(item)) }
                        </Timeline>
                        {(items?.length || 0) > previewLimit && !showAll && (
                            <div className='text-center'>
                                <Button
                                    title={t('logs.show_all_activity')}
                                    onClick={() => setShowAll(true)}
                                    color='none'
                                    textColor='gray-400'
                                />
                            </div>
                        )}
                        </div>
                        {showJumpRail && pin.height > 0 && createPortal(
                            <aside
                                className="pointer-events-none hidden w-10 md:block"
                                style={{
                                    position: 'fixed',
                                    top: pin.top,
                                    left: pin.left,
                                    height: pin.height,
                                    // Below Flowbite Modal / topbar (z-50) so lightboxes cover the rail.
                                    zIndex: 40,
                                }}
                            >
                                <MessageJumpRail
                                    messages={jumpMessages}
                                    activeId={activeJumpId}
                                    onJump={jumpToMessage}
                                />
                            </aside>,
                            document.body
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ChangeActivity;
